import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion as m } from "framer-motion";
import yp1 from "../images/yp1.jpeg";
import yp2 from "../images/yp2.jpeg";
import yp3 from "../images/yp3.jpeg";
import yp4 from "../images/yp4.jpeg";
import yp5 from "../images/yp5.jpeg";
import ypg1 from "../images/ypg1.jpeg";
import ypg2 from "../images/ypg2.jpeg";
import ypg3 from "../images/ypg3.jpeg";
import ypg4 from "../images/ypg4.jpeg";
import ypg5 from "../images/ypg5.jpeg";
import ypg6 from "../images/ypg6.jpeg";
import ypg7 from "../images/ypg7.jpeg";
import ypg8 from "../images/ypg8.jpeg";
import ypg9 from "../images/ypg9.jpeg";
import ypg10 from "../images/ypg10.jpeg";
import ypg11 from "../images/ypg11.jpeg";
import ypg12 from "../images/ypg12.jpeg";

const YouthProgram = () => {
  const [activeImg, setActiveImg] = useState(null);

  const heroImg = yp1;

  const sectionImages = {
    howKidsLearn: yp2,
    selfDefense: yp3,
    safetyStandards: yp4,
    ageStructure: yp5,
  };

  const galleryImages = useMemo(
    () => [ypg1, ypg2, ypg3, ypg4, ypg5, ypg6, ypg7, ypg8, ypg9, ypg10, ypg11, ypg12],
    []
  );

  const openModal = (img) => setActiveImg(img);
  const closeModal = () => setActiveImg(null);

  // --- Auto-scroll gallery (still manually scrollable) ---
  const galleryRef = useRef(null);
  const isUserInteractingRef = useRef(false);
  const resumeTimeoutRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);

  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;

    const PX_PER_SEC = 18; // slow drift; adjust as desired

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (!isUserInteractingRef.current) {
        el.scrollLeft += PX_PER_SEC * dt;

        // loop back to start when reaching end
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll > 0 && el.scrollLeft >= maxScroll - 1) {
          el.scrollLeft = 0;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    const pauseThenResume = () => {
      isUserInteractingRef.current = true;
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 1500);
    };

    const onPointerDown = () => pauseThenResume();
    const onPointerUp = () => pauseThenResume();
    const onWheel = () => pauseThenResume();
    const onTouchStart = () => pauseThenResume();
    const onTouchMove = () => pauseThenResume();

    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointerup", onPointerUp, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  return (
    <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.75, ease: "easeOut" }}>
      <div className="flex justify-center pt-28 pb-20">
        <div className="bg-black text-white w-full max-w-6xl rounded-lg px-4 sm:px-6 md:px-10 py-8">
          {/* HERO */}
          <div className="space-y-4">
            <h1 className="font-bold text-4xl sm:text-5xl">Westmore Youth Program</h1>

            <button
              type="button"
              onClick={() => openModal(heroImg)}
              className="w-full text-left"
              aria-label="Open hero image"
            >
              <img
                src={heroImg}
                alt="Westmore youth programs"
                className="w-full rounded-lg border border-white/10 object-cover max-h-[320px] sm:max-h-[420px]"
                loading="lazy"
              />
            </button>

            <div className="space-y-3 text-base sm:text-lg leading-relaxed text-white/90">
              <p>
                Westmore Youth Programs provide structured Brazilian Jiu Jitsu instruction for children ages 5–12.
                Programs support physical confidence, coordination, and personal safety through guided, rule based
                activities in a supervised environment.
              </p>
              <p>
                Westmore is a locally established Brazilian Jiu Jitsu academy that is highly rated by families and
                students in the community, reflecting consistent positive feedback on Google.
              </p>
              <p>
                Instruction emphasizes awareness, control, and responsible decision making. Students learn how to
                manage physical contact safely, recognize boundaries, and respond appropriately in common self-defense
                situations without reliance on strength or aggression.
              </p>
            </div>
          </div>

          <Section
            title="How Kids Learn at Westmore"
            image={sectionImages.howKidsLearn}
            imageAlt="Youth class learning through structured games"
            onImageClick={openModal}
          >
            <p>
              Youth classes are taught primarily through structured, goal-oriented games that use clear constraints
              directly related to Brazilian Jiu Jitsu. Each game is intentionally designed to teach balance,
              positioning, movement, and control while keeping students engaged and focused.
            </p>
          </Section>

          <Section title="Self-Defense Focus" image={sectionImages.selfDefense} imageAlt="Youth self-defense focus" onImageClick={openModal}>
            <p>
              Brazilian Jiu Jitsu provides a practical foundation for youth self-defense by emphasizing leverage,
              positioning, and awareness rather than striking or force. Self-defense is taught as personal safety and
              situational awareness with emphasis on avoidance and restraint.
            </p>
          </Section>

          <Section
            title="Safety and Instruction Standards"
            image={sectionImages.safetyStandards}
            imageAlt="Safety and supervision"
            onImageClick={openModal}
          >
            <ul className="list-disc pl-5 space-y-2">
              <li>All youth classes are supervised and instructor led.</li>
              <li>Instructors complete DOJ background checks.</li>
              <li>
                Activities are rule governed, controlled, and monitored to maintain a professional environment
                appropriate for children.
              </li>
            </ul>
          </Section>

          <Section
            title="Age Range and Class Structure"
            image={sectionImages.ageStructure}
            imageAlt="Youth class structure"
            onImageClick={openModal}
          >
            <p>
              Westmore Youth Programs serve children ages 5–12. Students are grouped by age, size, and skill level
              within class sessions. Groupings evolve as students progress.
            </p>
          </Section>

          {/* SCHEDULE */}
          <div className="mt-10 space-y-4">
            <h2 className="font-bold text-2xl sm:text-3xl underline">Class Schedule</h2>

            <div className="rounded-lg border border-white/10 bg-white/5 p-4 sm:p-5 space-y-3">
              <ScheduleRow day="Tuesday" time="4:30–5:30 PM" detail="No-Gi Training" />
              <ScheduleRow day="Thursday" time="4:30–5:30 PM" detail="Gi Training" />
              <div className="pt-2 text-white/80 text-sm sm:text-base">
                Loaner gis are available. Purchasing a gi is encouraged upon enrollment.
              </div>
            </div>
          </div>

          {/* BOTTOM MINI-GALLERY (auto-scroll + manual scroll) */}
          <div className="mt-10 space-y-4">
            {galleryImages.length > 0 ? (
              <div ref={galleryRef} className="flex gap-3 overflow-x-auto pb-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => openModal(img)}
                    className="shrink-0 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    aria-label={`Open photo ${idx + 1}`}
                  >
                    <img
                      src={img}
                      alt={`Westmore youth photo ${idx + 1}`}
                      className="h-24 w-32 sm:h-28 sm:w-40 object-cover rounded-lg"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-white/5 p-6">
                <p className="text-white/80 text-sm">Gallery placeholder.</p>
              </div>
            )}
          </div>

          {/* MODAL */}
          {activeImg && (
            <div
              className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-4"
              onClick={closeModal}
              role="dialog"
              aria-modal="true"
            >
              <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end pb-2">
                  {/* Close button with ZERO opacity */}
                  <button
                    type="button"
                    onClick={closeModal}
                    className="opacity-100 px-3 py-2 rounded-lg bg-black border border-white text-sm"
                  >
                    Close
                  </button>
                </div>
                <img
                  src={activeImg}
                  alt="Expanded"
                  className="w-full max-h-[80vh] object-contain rounded-lg border border-white"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </m.div>
  );
};

function Section({ title, children, image, imageAlt, onImageClick }) {
  return (
    <div className="mt-10">
      <h2 className="font-bold text-2xl sm:text-3xl underline">{title}</h2>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="text-base sm:text-lg leading-relaxed text-white/90 space-y-3">{children}</div>

        <div className="md:pt-1">
          <button
            type="button"
            onClick={() => onImageClick(image)}
            className="w-full text-left"
            aria-label={`Open image for ${title}`}
          >
            <img
              src={image}
              alt={imageAlt}
              className="w-full rounded-lg border border-white/10 object-cover max-h-[320px]"
              loading="lazy"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleRow({ day, time, detail }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 border-b border-white/10 pb-3">
      <div className="font-semibold text-lg">{day}</div>
      <div className="text-white/90">{time}</div>
      <div className="text-white/80">{detail}</div>
    </div>
  );
}

export default YouthProgram;
